import { z } from 'zod';

export type ManifestValidationMessages = {
  manifestValidationRequired?: string;
  manifestValidationEmailInvalid?: string;
  manifestValidationDateFuture?: string;
  manifestValidationDatePast?: string;
  manifestValidationValidUntilAfterIssue?: string;
  manifestValidationMustAccept?: string;
  manifestValidationPhoneOrMobile?: string;
  manifestValidationEmergencyBothOrNeither?: string;
  manifestValidationValidUntilAfterEmbark?: string;
  manifestValidationZipCodeFormat?: string;
  manifestValidationPhoneFormat?: string;
  manifestValidationPassportNumberFormat?: string;
};

export type ManifestSchemaOptions = {
  /** First trip's startDate (ISO YYYY-MM-DD) — embark date. */
  embarkDate?: string;
  /** Last trip's endDate (ISO YYYY-MM-DD) — debark date. */
  debarkDate?: string;
};

const parseIsoDate = (v: string): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

// Country-specific postal code shapes. Countries not listed accept any
// non-empty value (the per-field `.min(1, required)` still guards emptiness).
// Pattern is matched against trimmed input.
const ZIP_CODE_PATTERNS: Record<string, RegExp> = {
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  CH: /^\d{4}$/,
};

/**
 * Single schema covering every within-section rule for the manifest form.
 * Cross-source rules (embark/debark, indiaCruise, pregnancy-age) live in
 * follow-up work because they need data the form itself doesn't own.
 *
 * Messages come from the Prism block's `i18n` prop (server-baked via the
 * manifest-form transformer), NOT from `useClientTranslation` — the
 * dashboard's client i18n doesn't ship these keys.
 */
export const createManifestSchema = (
  i18n?: ManifestValidationMessages,
  opts?: ManifestSchemaOptions,
) => {
  const required =
    i18n?.manifestValidationRequired ?? 'Dieses Feld darf nicht leer sein.';
  const emailInvalid =
    i18n?.manifestValidationEmailInvalid ??
    'Bitte geben Sie eine gültige E-Mail-Adresse an.';
  const dateInFuture =
    i18n?.manifestValidationDateFuture ?? 'Datum muss in der Zukunft liegen.';
  const dateInPast =
    i18n?.manifestValidationDatePast ??
    'Datum muss in der Vergangenheit liegen.';
  const validUntilAfterIssue =
    i18n?.manifestValidationValidUntilAfterIssue ??
    'Gültig-bis-Datum muss nach dem Ausstellungsdatum liegen.';
  const validUntilAfterEmbark =
    i18n?.manifestValidationValidUntilAfterEmbark ??
    'Der Ausweis muss bis zum Reiseende ausreichend gültig sein.';
  const mustAccept = i18n?.manifestValidationMustAccept ?? 'Bitte bestätigen.';
  const phoneOrMobile =
    i18n?.manifestValidationPhoneOrMobile ??
    'Bitte geben Sie eine Telefon- oder Mobilnummer an.';
  const emergencyBoth =
    i18n?.manifestValidationEmergencyBothOrNeither ??
    'Bitte geben Sie sowohl Name als auch Telefonnummer an.';
  const zipCodeFormat =
    i18n?.manifestValidationZipCodeFormat ??
    'Bitte geben Sie eine gültige Postleitzahl an.';
  const phoneFormat =
    i18n?.manifestValidationPhoneFormat ??
    'Bitte geben Sie eine gültige Telefonnummer an.';
  const passportNumberFormat =
    i18n?.manifestValidationPassportNumberFormat ??
    'Bitte geben Sie eine gültige Ausweis-/Passnummer an.';

  const today = startOfToday();
  const embarkDate = opts?.embarkDate
    ? parseIsoDate(opts.embarkDate)
    : null;

  const mustBeTrue = z
    .boolean()
    .refine(v => v === true, { message: mustAccept });

  return z.object({
    personalInfo: z.object({
      salutation: z.string(),
      title: z.string(),
      firstName: z.string().min(1, required),
      lastName: z.string().min(1, required),
    }),

    contact: z
      .object({
        streetAddress: z.string().min(2, required),
        zipCode: z.string().min(1, required),
        city: z.string().min(1, required),
        country: z.string().min(1, required),
        email: z.string().min(1, required).email(emailInvalid),
        phone: z.string(),
        mobile: z.string(),
        approveMobilePhoneDisclosure: z.boolean(),
      })
      .superRefine((data, ctx) => {
        if (!data.phone && !data.mobile) {
          ctx.addIssue({
            code: 'custom',
            message: phoneOrMobile,
            path: ['phone'],
          });
          ctx.addIssue({
            code: 'custom',
            message: phoneOrMobile,
            path: ['mobile'],
          });
        }

        const zipPattern = ZIP_CODE_PATTERNS[data.country];
        if (zipPattern && data.zipCode && !zipPattern.test(data.zipCode.trim())) {
          ctx.addIssue({
            code: 'custom',
            message: zipCodeFormat,
            path: ['zipCode'],
          });
        }

        // Phone format: digits only (spaces already stripped by the phone
        // field wrapper), min 8 digits. Only runs when the user entered
        // something — emptiness is covered by the phoneOrMobile check above.
        const phoneDigits = data.phone.replace(/\s+/g, '');
        if (phoneDigits && !/^\+?\d{8,}$/.test(phoneDigits)) {
          ctx.addIssue({
            code: 'custom',
            message: phoneFormat,
            path: ['phone'],
          });
        }
        const mobileDigits = data.mobile.replace(/\s+/g, '');
        if (mobileDigits && !/^\+?\d{8,}$/.test(mobileDigits)) {
          ctx.addIssue({
            code: 'custom',
            message: phoneFormat,
            path: ['mobile'],
          });
        }
      }),

    credentials: z
      .object({
        passportType: z.string().min(1, required),
        nationality: z.string().min(1, required),
        passportNumber: z.string().min(1, required),
        placeOfBirth: z.string().min(1, required),
        placeOfIssue: z.string().min(1, required),
        dateOfBirth: z.string().min(1, required),
        dateOfIssue: z.string().min(1, required),
        birthName: z.string(),
        validUntil: z.string().min(1, required),
        profession: z.string().min(1, required),
        entryRequirementsAccepted: mustBeTrue,
        hasVisa: z.boolean(),
      })
      .superRefine((data, ctx) => {
        const dob = parseIsoDate(data.dateOfBirth);
        const doi = parseIsoDate(data.dateOfIssue);
        const vu = parseIsoDate(data.validUntil);

        if (dob && dob > today) {
          ctx.addIssue({
            code: 'custom',
            message: dateInPast,
            path: ['dateOfBirth'],
          });
        }
        if (doi && doi > today) {
          ctx.addIssue({
            code: 'custom',
            message: dateInPast,
            path: ['dateOfIssue'],
          });
        }
        if (vu && vu <= today) {
          ctx.addIssue({
            code: 'custom',
            message: dateInFuture,
            path: ['validUntil'],
          });
        }
        if (doi && vu && vu <= doi) {
          ctx.addIssue({
            code: 'custom',
            message: validUntilAfterIssue,
            path: ['validUntil'],
          });
        }

        // Passport/id number: alphanumeric (letters + digits only), min 5.
        // Only runs when the user entered something — emptiness is covered
        // by `.min(1, required)`.
        if (data.passportNumber && !/^[A-Za-z0-9]{5,}$/.test(data.passportNumber)) {
          ctx.addIssue({
            code: 'custom',
            message: passportNumberFormat,
            path: ['passportNumber'],
          });
        }

        // Cross-source: date of issue must be before the embark date.
        if (doi && embarkDate && doi >= embarkDate) {
          ctx.addIssue({
            code: 'custom',
            message: dateInPast,
            path: ['dateOfIssue'],
          });
        }

        // Cross-source: id must remain valid past a country/type-specific
        // grace period after embark. Reisepass = 6 months; Personalausweis
        // = 3 months. Other passport types fall through (no rule).
        if (vu && embarkDate) {
          const monthsRequired =
            data.passportType === 'Reisepass'
              ? 6
              : data.passportType === 'Personalausweis'
                ? 3
                : 0;
          if (monthsRequired > 0) {
            const minValidUntil = addMonths(embarkDate, monthsRequired);
            if (vu < minValidUntil) {
              ctx.addIssue({
                code: 'custom',
                message: validUntilAfterEmbark,
                path: ['validUntil'],
              });
            }
          }
        }
      }),

    otherInformation: z
      .object({
        mealRequest: z.string(),
        individualRequest: z.string(),
        allergy: z.array(z.string()),
        allergyInfo: z.string(),
        handicap: z.string(),
        emergencyContactName: z.string(),
        emergencyContactPhone: z.string(),
        acceptDataProtectionTerms: z.boolean(),
        acceptHealthTerms: mustBeTrue,
        pregnant24Weeks: z.boolean(),
        preferredLanguageCode: z.string().min(1, required),
      })
      .superRefine((data, ctx) => {
        const nameSet = !!data.emergencyContactName;
        const phoneSet = !!data.emergencyContactPhone;
        if (nameSet !== phoneSet) {
          if (!nameSet) {
            ctx.addIssue({
              code: 'custom',
              message: emergencyBoth,
              path: ['emergencyContactName'],
            });
          }
          if (!phoneSet) {
            ctx.addIssue({
              code: 'custom',
              message: emergencyBoth,
              path: ['emergencyContactPhone'],
            });
          }
        }

        const hasHealthData =
          !!data.mealRequest ||
          !!data.individualRequest ||
          data.allergy.length > 0 ||
          !!data.allergyInfo ||
          !!data.handicap;
        if (hasHealthData && !data.acceptDataProtectionTerms) {
          ctx.addIssue({
            code: 'custom',
            message: mustAccept,
            path: ['acceptDataProtectionTerms'],
          });
        }
      }),
  });
};
